'use client';

import clsx from 'clsx';
import { KeyboardEvent, RefObject } from 'react';

export type CalendarCell = {
    date: Date;
    iso: string;
    isCurrentMonth: boolean;
};

type DatePickerPanelProps = {
    panelId: string;
    panelRef: RefObject<HTMLDivElement | null>;
    panelPosition: { top: number; left: number; width: number };
    panelLayer: number;
    monthLabel: string;
    weekDays: string[];
    calendarDays: CalendarCell[];
    selectedIso: string;
    todayIso: string;
    activeIso: string;
    onPanelKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
    onPreviousMonth: () => void;
    onNextMonth: () => void;
    onSelectDate: (date: Date) => void;
    onHoverDate: (date: Date) => void;
    onClearDate: () => void;
};

export const DatePickerPanel = ({
    panelId,
    panelRef,
    panelPosition,
    panelLayer,
    monthLabel,
    weekDays,
    calendarDays,
    selectedIso,
    todayIso,
    activeIso,
    onPanelKeyDown,
    onPreviousMonth,
    onNextMonth,
    onSelectDate,
    onHoverDate,
    onClearDate,
}: DatePickerPanelProps) => {
    return (
        <div
            id={panelId}
            ref={panelRef}
            role="dialog"
            aria-label="Выбор даты"
            tabIndex={-1}
            data-click-outside-ignore="true"
            data-ui="date-picker-panel"
            onKeyDown={onPanelKeyDown}
            className="pointer-events-auto fixed rounded-lg border border-slate-200 bg-white p-2 shadow-xl"
            style={{
                top: panelPosition.top,
                left: panelPosition.left,
                width: panelPosition.width,
                zIndex: panelLayer,
            }}
        >
            <div className="mb-2 flex items-center justify-between">
                <button
                    type="button"
                    className="h-8 w-8 rounded border border-slate-200 text-slate-700 hover:bg-slate-100"
                    onClick={onPreviousMonth}
                    aria-label="Предыдущий месяц"
                >
                    {'<'}
                </button>
                <p className="text-xs font-medium capitalize text-slate-800">{monthLabel}</p>
                <button
                    type="button"
                    className="h-8 w-8 rounded border border-slate-200 text-slate-700 hover:bg-slate-100"
                    onClick={onNextMonth}
                    aria-label="Следующий месяц"
                >
                    {'>'}
                </button>
            </div>
            <div className="mb-1 grid grid-cols-7 gap-1">
                {weekDays.map((day) => (
                    <span key={day} className="text-center text-[11px] font-medium text-slate-500">
                        {day}
                    </span>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day) => {
                    const isSelected = day.iso === selectedIso;
                    const isToday = day.iso === todayIso;
                    const isActive = day.iso === activeIso;

                    return (
                        <button
                            type="button"
                            key={day.iso}
                            className={clsx(
                                'h-8 rounded text-xs transition-colors',
                                day.isCurrentMonth ? 'text-slate-800' : 'text-slate-400',
                                isToday && 'font-semibold',
                                isActive && 'ring-1 ring-blue-400',
                                isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'hover:bg-slate-100',
                            )}
                            onClick={() => onSelectDate(day.date)}
                            onMouseEnter={() => onHoverDate(day.date)}
                        >
                            {day.date.getDate()}
                        </button>
                    );
                })}
            </div>
            <div className="mt-2 flex justify-between gap-2">
                <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                    onClick={() => onSelectDate(new Date())}
                >
                    Сегодня
                </button>
                <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                    onClick={onClearDate}
                >
                    Очистить
                </button>
            </div>
        </div>
    );
};
